import React, { useEffect, useState } from "react";
import { ReferenceDetail } from "../../interfaces/referenceDetaill";
import { formatDate } from "../../services/helpers";
import { getReferenceDetailByID } from "../../services/referenceDetailService";
import { ReferenceOrder } from "../../interfaces/referenceOrder";
import {
  getCandidateNotifiedDatesByReferenceOrderID,
  getReferenceOrderByID,
} from "../../services/referenceOrderService";
import { getOrderByID } from "../../services/orderService";
import { getOrderFormByOrderIDType } from "../../services/orderFormService";
import { OrderForm } from "../../interfaces/orderForm";
import { Order } from "../../interfaces/order";
import { getClient } from "../../services/clientService";
import {
  getReferenceQuestionnaireBase,
  getReferenceQuestionnaireQuestionOptions,
  getReferenceQuestionnaireQuestions,
  sendReferenceQuestionnairePdf,
} from "../../services/referenceQuestionnaireService";
import { getAdditionalForm } from "../../services/additionalFormService";
import { createReferenceLetter } from "../../services/referenceLetterService";
import { ReferenceLetter } from "../../interfaces/referenceLetter";
import { Client } from "../../interfaces/client";
import { AdditionalForm } from "../../interfaces/additionalForm";
import ToolTip from "../component-tools/ToolTip";
import { ROCandidateNotifiedDate } from "../../interfaces/roCandidateNotifiedDate";
import { sendNotifyClientEmail } from "../../services/emailService";
import { NotificationType } from "../../interfaces/clientNotification";

interface Props {
  referenceDetailID: number;
  onClose: () => void;
  onSuccess: () => void;
}

const SendLetterModal: React.FC<Props> = ({
  referenceDetailID,
  onClose,
  onSuccess,
}) => {
  const [referenceDetail, setReferenceDetail] =
    useState<ReferenceDetail | null>(null);
  const [referenceOrder, setReferenceOrder] = useState<ReferenceOrder | null>(
    null,
  );
  const [loadingSpinner, setLoadingSpinner] = useState(true);
  const [authForm, setAuthForm] = useState<OrderForm | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [newLetter, setNewLetter] = useState<ReferenceLetter>({
    referenceLetterID: 0,
    referenceDetailID: referenceDetail?.referenceDetailID || 0,
    letterDate: new Date(),
    certified: false,
    sentByMail: false,
    ignore: false,
  });
  const [client, setClient] = useState<Client>();
  const [signatureForm, setSignatureForm] = useState<AdditionalForm | null>(
    null,
  );
  const [notifyCandidate, setNotifyCandidate] = useState(false);
  const [candidateNotifiedDates, setCandidateNotifiedDates] =
    useState<ROCandidateNotifiedDate[]>();

  useEffect(() => {
    const fetchReferenceOrderDetails = async () => {
      try {
        setLoadingSpinner(true);
        if (referenceDetailID) {
          const detailResponse =
            await getReferenceDetailByID(referenceDetailID);
          setReferenceDetail(detailResponse);

          const referenceOrderResponse = await getReferenceOrderByID(
            detailResponse.referenceOrderID,
          );
          setReferenceOrder(referenceOrderResponse);

          // get auth form
          const order = await getOrderByID(referenceOrderResponse.orderID);
          setOrder(order);
          const authForm = await getOrderFormByOrderIDType(order.orderID, 5);
          setAuthForm(authForm);

          // get signature form
          const client = await getClient(order.clientID);
          setClient(client);
          const signatureForm = await getAdditionalForm(client.clientID, 1);
          if (signatureForm) {
            setSignatureForm(signatureForm);
          }

          const candidateDatesResponse =
            await getCandidateNotifiedDatesByReferenceOrderID(
              referenceOrderResponse.referenceOrderID,
            );
          setCandidateNotifiedDates(candidateDatesResponse);
        }
      } catch (err) {
        console.log("Failed to fetch reference detail.");
      } finally {
        setLoadingSpinner(false);
      }
    };

    fetchReferenceOrderDetails();
  }, [referenceDetailID]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
  ) => {
    let value: string | boolean =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (newLetter && field in newLetter) {
      setNewLetter({
        ...newLetter,
        [field]: value,
      });
    }
  };

  const handleSendLetter = async () => {
    try {
      setLoadingSpinner(true);

      if (referenceOrder && order && client) {
        const referenceQuestionnaire = await getReferenceQuestionnaireBase(
          client.clientID,
        );
        const questions = await getReferenceQuestionnaireQuestions(
          referenceQuestionnaire.referenceQuestionnaireID,
        );

        const questionsWithOptionsMap = new Map();
        for (const question of questions) {
          const options = await getReferenceQuestionnaireQuestionOptions(
            question.questionID,
          );
          questionsWithOptionsMap.set(question.questionID, {
            questionText: question.questionText,
            options: options,
          });
        }

        const logoForm = await getAdditionalForm(client.clientID, 5);

        if (referenceDetail) {
          const sendSuccess = await sendReferenceQuestionnairePdf(
            referenceDetail,
            order,
            client,
            referenceQuestionnaire,
            signatureForm || null,
            authForm || null,
            logoForm || null,
            questionsWithOptionsMap,
            newLetter.sentByMail,
            client.diocese ? client.diocese : "",
            client.diocese2 ? client.diocese2 : "",
          );

          if (sendSuccess) {
            newLetter.referenceDetailID = referenceDetailID;
            if (newLetter.sentByMail) {
              newLetter.address =
                referenceDetail.referenceAddress +
                " " +
                (referenceDetail.referenceAddress2 ?? "") +
                ", " +
                referenceDetail.referenceCity +
                ", " +
                referenceDetail.referenceState +
                " " +
                referenceDetail.referenceZipCode;
            } else {
              newLetter.address = referenceDetail.referenceEmail;
            }
            await createReferenceLetter(newLetter);

            //notify candidate if checked
            if (notifyCandidate && referenceOrder) {
              await sendNotifyClientEmail({
                orderId: order.orderID,
                type: NotificationType.NotifyCandidate,
              });
            }
          } else {
            throw new Error("Failed to send the reference questionnaire.");
          }
        }
      }
    } catch (error) {
      console.error("Error sending the letter:", error);
    } finally {
      setLoadingSpinner(false);
      onClose();
      onSuccess();
    }
  };

  return (
    <>
      {loadingSpinner ? (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <>
          <div className="background-mask" onClick={onClose}></div>
          <div className="filter-popup">
            <div className="close-button-container">
              <button className="close-button" onClick={onClose}>
                <i className="bi bi-x-circle"></i>
              </button>
            </div>

            <div className="filter-header">
              <h2>Send Letter</h2>
              {(!authForm?.completedFormUrl ||
                !signatureForm?.completedFormUrl) && (
                <div className="error">
                  You cannot send letters without a candidate reference
                  authorization form and client signature on file.
                </div>
              )}
              <div>
                If not sending by mail, email with reference form link will be
                sent to the email below.
              </div>
            </div>

            <br />

            <div className="dialog-container">
              <div className="field-long" style={{ flex: "0 0 45%" }}>
                <label className="field-label">Reference Name:</label>
                <div className="field-input">
                  <input
                    className="form-control"
                    type="text"
                    value={referenceDetail?.referenceName || ""}
                    disabled
                  />
                </div>
              </div>

              <div className="field-long" style={{ flex: "0 0 45%" }}>
                <label className="field-label">Contact Email:</label>
                <div className="field-input">
                  <input
                    className="form-control"
                    type="text"
                    value={referenceDetail?.referenceEmail || ""}
                    disabled
                  />
                </div>
              </div>

              <div className="field-long" style={{ flex: "0 0 20%" }}>
                <label className="field-label">Letter Date:</label>
                <div className="field-input">
                  <input
                    className="form-control"
                    type="date"
                    value={formatDate(newLetter?.letterDate)}
                    disabled
                  />
                </div>
              </div>

              {order?.candidateEmail && (
                <div
                  className="field-long"
                  style={{ flex: "0 0 100%", flexWrap: "wrap" }}
                >
                  <label className="field-label">Notify Candidate:</label>
                  <div>
                    <input
                      className={`form-check-input ${
                        !referenceOrder ? "disabled" : ""
                      }`}
                      type="checkbox"
                      checked={notifyCandidate || false}
                      onChange={() => setNotifyCandidate(!notifyCandidate)}
                      disabled={!referenceOrder}
                    />
                  </div>
                  <ToolTip
                    tooltipText="Sends an email notifying the candidate of unresponsive references."
                    placement="right"
                    className="info-icon-spec"
                  ></ToolTip>
                  {candidateNotifiedDates?.map((date, index) => (
                    <div key={date.notifiedDateID || index}>
                      {formatDate(date.notifiedDate, "us")}
                    </div>
                  ))}
                </div>
              )}

              <div className="field-long" style={{ flex: "0 0 100%" }}>
                <label className="field-label">Send By Mail? :</label>
                <div>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={newLetter?.sentByMail || false}
                    onChange={(e) => handleInputChange(e, "sentByMail")}
                  />
                </div>
              </div>

              {newLetter?.sentByMail && (
                <>
                  <div style={{ paddingTop: "10px" }}>
                    <div className="field-long" style={{ flex: "0 0 100%" }}>
                      <label className="field-label">Certified? :</label>
                      <div>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={newLetter?.certified || false}
                          onChange={(e) => handleInputChange(e, "certified")}
                        />
                      </div>
                    </div>
                    <div style={{ fontSize: "small" }}>
                      No action will be taken by system. Admin is in charge of
                      mailing form. PDF will be downloaded to browser.
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="filter-actions">
              <button
                className={`smbtn btn-green ${
                  !authForm?.completedFormUrl ||
                  !signatureForm?.completedFormUrl
                    ? "disabled"
                    : ""
                }`}
                onClick={handleSendLetter}
                disabled={
                  !authForm?.completedFormUrl ||
                  !signatureForm?.completedFormUrl
                }
              >
                Send Letter
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SendLetterModal;
